import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Menu, X } from "lucide-react";
import { Link, useLocation } from 'react-router-dom';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import OptimizedImage from "@/components/OptimizedImage";
import xeniraLogo from "@/assets/xenira-logo.png";
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setIsScrolled(scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track which section is in view
  useEffect(() => {
    const featuresSection = document.getElementById('features');
    if (!featuresSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setActiveSection('features');
      } else {
        setActiveSection(null);
      }
    }, {
      threshold: 0.3
    });
    observer.observe(featuresSection);
    return () => observer.disconnect();
  }, [location.pathname]);
  const toggleMenu = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);

    // Prevent body scroll when mobile menu is open
    if (newState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
    setIsMenuOpen(false);
    document.body.style.overflow = '';
  };
  const isActive = (path: string) => location.pathname === path;
  const isTechnologyActive = () => {
    return location.pathname === '/' && location.hash === '#features' || location.pathname === '/' && activeSection === 'features';
  };
  const navLinkClasses = (path: string) => cn("relative px-4 py-2.5 text-sm font-medium transition-all duration-300 rounded-lg", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[60%] after:h-[2px] after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isActive(path) ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "text-gray-800 hover:text-gray-900 hover:bg-gray-100 after:scale-x-0 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]");
  const technologyLinkClasses = () => cn("relative px-4 py-2.5 text-sm font-medium transition-all duration-300 rounded-lg", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[60%] after:h-[2px] after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isTechnologyActive() ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "text-gray-800 hover:text-gray-900 hover:bg-gray-100 after:scale-x-0 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]");
  return <nav className="sticky top-0 left-0 right-0 z-[9999] w-full bg-white shadow-sm pointer-events-auto">
      
      <div className="relative w-full px-8 lg:px-12 mx-auto">
        <div className="flex items-center justify-between h-20 md:h-24">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <OptimizedImage src={xeniraLogo} alt="XENIRA Logo" className="h-10 sm:h-12 md:h-14 lg:h-16 xl:h-18 w-auto transition-all duration-300 group-hover:scale-105" sizes="200px" priority={true} />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <NavigationMenu>
              <NavigationMenuList className="space-x-2">
                
                <NavigationMenuItem>
                  <Link to="/about" className={navLinkClasses('/about')} aria-current={isActive('/about') ? 'page' : undefined}>
                    About Us
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link to="/#features" className={technologyLinkClasses()} aria-current={isTechnologyActive() ? 'page' : undefined}>
                    Technology
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link to="/development-process" className={navLinkClasses('/development-process')} aria-current={isActive('/development-process') ? 'page' : undefined}>
                    Development
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link to="/manufacturing" className={navLinkClasses('/manufacturing')} aria-current={isActive('/manufacturing') ? 'page' : undefined}>
                    Manufacturing
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link to="/partnership" className={navLinkClasses('/partnership')} aria-current={isActive('/partnership') ? 'page' : undefined}>Partnership</Link>
                </NavigationMenuItem>
                
                
                
                <NavigationMenuItem>
                  <button onClick={() => scrollToSection('contact-info')} className="rounded-xl text-sm font-semibold transition-all duration-300 text-white shadow-lg hover:shadow-2xl hover:scale-110 transform bg-gradient-to-r from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] hover:from-[hsl(var(--color-3))] hover:to-[hsl(var(--color-4))] border-2 border-[hsl(var(--color-1))] hover:border-[hsl(var(--color-3))] py-[5px] px-[15px] shadow-[hsl(var(--color-1))]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2" aria-label="Navigate to contact information section">
                    Contact Us
                  </button>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
          
          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button onClick={toggleMenu} className="p-2 transition-all duration-300 hover:bg-gray-100 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2" aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"} aria-expanded={isMenuOpen} aria-controls="mobile-navigation">
              {isMenuOpen ? <X size={24} className="text-gray-800" /> : <Menu size={24} className="text-gray-800" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu - Increased height to show all items */}
      <div id="mobile-navigation" className={cn("lg:hidden transition-all duration-300 overflow-hidden w-full", isMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0")}>
        <div className="px-6 pt-4 pb-6 space-y-3 bg-white border-t border-gray-200 shadow-lg">
          <Link to="/about" className={cn("block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:block after:h-[2px] after:w-[30%] after:ml-0 after:mt-2 after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isActive('/about') ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "hover:bg-gray-50 text-gray-800 after:scale-x-0")} aria-current={isActive('/about') ? 'page' : undefined} onClick={() => {
          setIsMenuOpen(false);
          window.scrollTo(0, 0);
        }}>
            About Us
          </Link>
          
          <Link to="/#features" onClick={() => setIsMenuOpen(false)} className={cn("block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:block after:h-[2px] after:w-[30%] after:ml-0 after:mt-2 after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isTechnologyActive() ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "hover:bg-gray-50 text-gray-800 after:scale-x-0")} aria-current={isTechnologyActive() ? 'page' : undefined}>
            Technology
          </Link>
          
          <Link to="/development-process" className={cn("block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:block after:h-[2px] after:w-[30%] after:ml-0 after:mt-2 after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isActive('/development-process') ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "hover:bg-gray-50 text-gray-800 after:scale-x-0")} aria-current={isActive('/development-process') ? 'page' : undefined} onClick={() => {
          setIsMenuOpen(false);
          window.scrollTo(0, 0);
        }}>
            Development
          </Link>
          
          <Link to="/manufacturing" className={cn("block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:block after:h-[2px] after:w-[30%] after:ml-0 after:mt-2 after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isActive('/manufacturing') ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "hover:bg-gray-50 text-gray-800 after:scale-x-0")} aria-current={isActive('/manufacturing') ? 'page' : undefined} onClick={() => {
          setIsMenuOpen(false);
          window.scrollTo(0, 0);
        }}>
            Manufacturing
          </Link>
          
          <Link to="/partnership" className={cn("block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2", "after:content-[''] after:block after:h-[2px] after:w-[30%] after:ml-0 after:mt-2 after:transition-all after:duration-500 after:ease-in-out after:rounded-full", isActive('/partnership') ? "text-gray-900 font-semibold after:scale-x-100 after:bg-gradient-to-r after:from-[hsl(var(--color-1))] after:to-[hsl(var(--color-3))]" : "hover:bg-gray-50 text-gray-800 after:scale-x-0")} aria-current={isActive('/partnership') ? 'page' : undefined} onClick={() => {
          setIsMenuOpen(false);
          window.scrollTo(0, 0);
        }}>
            Partnerships
          </Link>
          
          <button onClick={() => scrollToSection('contact-info')} className="block w-full rounded-lg text-sm font-medium transition-all duration-300 text-white shadow-lg bg-gradient-to-r from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] hover:from-[hsl(var(--color-3))] hover:to-[hsl(var(--color-4))] py-[10px] px-[16px] text-center shadow-[hsl(var(--color-1))]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2" aria-label="Navigate to contact information section">
            Contact Us
          </button>
        </div>
      </div>
    </nav>;
};
export default Navbar;